/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Card,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { getApiBaseUrl } from "utils/apiBaseUrl";

const loadScript = (src, id) =>
  new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.id = id;
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.body.appendChild(script);
  });

const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join("; ");
  }
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return err?.message || "Something went wrong. Please try again.";
};

const isTimeoutError = (err) => {
  const code = err?.code;
  const message = String(err?.message || "").toLowerCase();
  return code === "ECONNABORTED" || message.includes("timeout");
};

export default function PublicPayPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const result = searchParams.get("result");
  const sessionId = searchParams.get("session_id");

  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const client = useMemo(
    () =>
      axios.create({
        baseURL: apiBaseUrl,
        timeout: 60000,
      }),
    [apiBaseUrl]
  );

  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [details, setDetails] = useState(null);
  const [checkout, setCheckout] = useState(null);

  const gateway = details?.gateway || "stripe";

  const refreshPaymentDetails = useCallback(async () => {
    if (!token) return null;
    const res = await client.get(`/payment-gateways/public/pay/${token}`);
    setDetails(res.data);
    return res.data;
  }, [token, client]);

  useEffect(() => {
    if (!token) return;
    let cancelledReq = false;
    setLoading(true);
    setError("");
    setDetails(null);
    setCheckout(null);
    setSuccess(false);
    setCancelled(false);

    client
      .get(`/payment-gateways/public/pay/${token}`)
      .then((res) => {
        if (cancelledReq) return;
        setDetails(res.data);
        if (res.data?.already_paid) {
          setSuccess(true);
        }
      })
      .catch((err) => {
        if (!cancelledReq) {
          setError(formatApiError(err) || "Invalid payment link.");
        }
      })
      .finally(() => {
        if (!cancelledReq) setLoading(false);
      });

    return () => {
      cancelledReq = true;
    };
  }, [token, client]);

  // After Stripe-hosted Checkout redirects back
  useEffect(() => {
    if (!token || !details || loading) return;

    if (result === "cancelled") {
      setCancelled(true);
      client.post(`/payment-gateways/public/${token}/cancelled`).catch(() => {});
      return;
    }

    if (result === "success") {
      // Webhook may have already marked Paid before confirm returns.
      if (details.already_paid) {
        setSuccess(true);
        setError("");
        return;
      }

      const confirm = async () => {
        setPaying(true);
        setError("");
        try {
          await client.post("/payment-gateways/public/confirm", {
            token,
            gateway: "stripe",
            stripe_checkout_session_id: sessionId || undefined,
          });
          setSuccess(true);
        } catch (err) {
          // Charge often succeeds; only the confirm HTTP call times out on PDF/email.
          try {
            const latest = await refreshPaymentDetails();
            if (latest?.already_paid) {
              setSuccess(true);
              setError("");
              return;
            }
          } catch (_) {
            // Fall through to surface the original error.
          }
          if (isTimeoutError(err)) {
            setError(
              "Payment may have succeeded, but confirmation is taking longer than expected. Please wait a moment and refresh — do not pay again."
            );
          } else {
            setError(formatApiError(err) || "Could not confirm payment.");
          }
        } finally {
          setPaying(false);
        }
      };
      confirm();
    }
  }, [token, details, loading, result, sessionId, client, refreshPaymentDetails]);

  const redirectToStripe = useCallback(
    async ({ manual = false } = {}) => {
      if (!token || !details || details.already_paid || checkoutLoading) return;
      if (result === "success" || result === "cancelled") return;

      const existingUrl = details.checkout_url || checkout?.checkout_url;
      if (existingUrl && !manual) {
        window.location.href = existingUrl;
        return;
      }

      setCheckoutLoading(true);
      setError("");
      try {
        const res = await client.post("/payment-gateways/public/checkout", {
          token,
          gateway: details.gateway || "stripe",
        });
        if (res.data?.already_paid) {
          setSuccess(true);
          return;
        }
        setCheckout(res.data);
        if (res.data?.checkout_url) {
          window.location.href = res.data.checkout_url;
          return;
        }
        setError("Stripe checkout URL was not returned. Please try again.");
      } catch (err) {
        setError(formatApiError(err) || "Unable to start checkout. Please try again.");
      } finally {
        setCheckoutLoading(false);
      }
    },
    [token, details, checkout, checkoutLoading, client, result]
  );

  // Stripe: immediately send the customer to checkout.stripe.com
  useEffect(() => {
    if (!details || details.already_paid || loading) return;
    if (result === "success" || result === "cancelled") return;
    if (gateway !== "stripe") return;
    redirectToStripe();
  }, [details, loading, gateway, result, redirectToStripe]);

  const confirmOnServer = async (payload) => {
    await client.post("/payment-gateways/public/confirm", { token, ...payload });
    setSuccess(true);
  };

  const payWithRazorpay = async () => {
    setPaying(true);
    setError("");
    try {
      let activeCheckout = checkout;
      if (!activeCheckout?.razorpay_order_id) {
        const res = await client.post("/payment-gateways/public/checkout", {
          token,
          gateway: "razorpay",
        });
        if (res.data?.already_paid) {
          setSuccess(true);
          return;
        }
        activeCheckout = res.data;
        setCheckout(res.data);
      }
      await loadScript("https://checkout.razorpay.com/v1/checkout.js", "razorpay-js");
      const options = {
        key: activeCheckout.razorpay_key_id,
        amount: Math.round(Number(details.amount) * 100),
        currency: (details.currency || "INR").toUpperCase(),
        name: details.company_name || "Pearl CRM",
        description: details.invoice_number ? `Invoice ${details.invoice_number}` : "Travel payment",
        order_id: activeCheckout.razorpay_order_id,
        handler: async (response) => {
          try {
            await confirmOnServer({
              gateway: "razorpay",
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
          } catch (err) {
            setError(formatApiError(err) || "Could not confirm payment.");
          }
        },
        modal: {
          ondismiss: () => client.post(`/payment-gateways/public/${token}/cancelled`).catch(() => {}),
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(formatApiError(err) || "Razorpay checkout failed.");
    } finally {
      setPaying(false);
    }
  };

  const paymentLabel =
    details?.payment_mode === "Installment" && details?.installment_number
      ? `Installment ${details.installment_number}`
      : details?.payment_mode || "Payment";

  const showStripeRedirect =
    gateway === "stripe" && !success && !cancelled && result !== "success" && result !== "cancelled";

  const canRetryStripeCheckout =
    error && gateway === "stripe" && !success && result !== "success" && !details?.already_paid;

  const checkPaymentStatus = async () => {
    setPaying(true);
    setError("");
    try {
      const latest = await refreshPaymentDetails();
      if (latest?.already_paid) {
        setSuccess(true);
        return;
      }
      if (result === "success" && sessionId) {
        await client.post("/payment-gateways/public/confirm", {
          token,
          gateway: "stripe",
          stripe_checkout_session_id: sessionId,
        });
        setSuccess(true);
        return;
      }
      setError(
        "Payment is not marked paid yet. If you were charged, please contact support — do not pay again."
      );
    } catch (err) {
      setError(formatApiError(err) || "Could not refresh payment status.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <MDBox minHeight="100vh" bgcolor="#f5f7fa" display="flex" alignItems="center" justifyContent="center" p={2}>
      <Card sx={{ p: 3, width: "100%", maxWidth: 520 }}>
        <MDTypography variant="h4" fontWeight="medium" mb={1}>
          {details?.company_name || "Secure payment"}
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={3}>
          {showStripeRedirect
            ? "Redirecting you to Stripe Checkout…"
            : "Complete your travel booking payment securely."}
        </MDTypography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment received. Thank you — you can close this page.
          </Alert>
        )}
        {cancelled && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Checkout was cancelled. You can close this page or try again from your payment link.
          </Alert>
        )}

        {(loading || paying || checkoutLoading || showStripeRedirect) && !success && !cancelled && !error && (
          <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
            <CircularProgress />
            <MDTypography variant="caption" color="text">
              {paying
                ? "Confirming payment…"
                : showStripeRedirect
                  ? "Opening Stripe Checkout…"
                  : "Loading payment details…"}
            </MDTypography>
          </MDBox>
        )}

        {!loading && details && !success && !cancelled && gateway === "razorpay" && (
          <>
            <MDBox display="flex" gap={1} flexWrap="wrap" mb={2}>
              <Chip label={paymentLabel} color="info" size="small" />
              {details.invoice_number && <Chip label={details.invoice_number} size="small" />}
            </MDBox>
            <MDTypography variant="h5" mb={2}>
              {details.currency} {Number(details.amount || 0).toFixed(2)}
            </MDTypography>

            {details.already_paid ? (
              <Alert severity="info">This payment has already been completed.</Alert>
            ) : (
              <MDBox mt={2}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={paying}
                  onClick={payWithRazorpay}
                >
                  {paying ? <CircularProgress size={20} color="inherit" /> : "Pay now"}
                </MDButton>
              </MDBox>
            )}
          </>
        )}

        {canRetryStripeCheckout && (
          <MDButton
            variant="outlined"
            color="info"
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => redirectToStripe({ manual: true })}
          >
            Retry Stripe Checkout
          </MDButton>
        )}

        {error && gateway === "stripe" && !success && result === "success" && (
          <MDButton
            variant="outlined"
            color="info"
            fullWidth
            sx={{ mt: 2 }}
            disabled={paying}
            onClick={checkPaymentStatus}
          >
            {paying ? <CircularProgress size={20} color="inherit" /> : "Check payment status"}
          </MDButton>
        )}
      </Card>
    </MDBox>
  );
}
